import React from 'react'

import { Box, Button, Tabs } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { BulkCreateAttendanceTimeBlockCompensationMutation } from 'generated/resources/graphql'
import { isEmpty } from 'lodash'
import { Moment } from 'moment'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import EmployeeTimeTrackingBreakdown from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown'
import CompensationResponse from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/CompensationResponse'
import CreateTimeBlockSupplementWizard from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/CreateTimeBlockSupplementWizard'
import PayrollSetupWarning from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/PayrollSetupWarning'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { useEmployeeSidepanelNavigation } from 'modules/attendance/hooks/useEmployeeSidepanelNavigation'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { SidePanelTab } from 'modules/attendance/lib/sidepanelTabTypes'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import BalanceCarryoverUtils from 'modules/attendance/lib/utils/BalanceCarryoverUtils'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

type AttendanceManualCompensation = NonNullable<
  NonNullable<
    BulkCreateAttendanceTimeBlockCompensationMutation['attendanceMutations']['bulkCreateTimeBlockSupplementAttendanceManualCompensation']
  >['manualCompensation']
>

type Props = {
  tabFromNavigation: SidePanelTab
  timeSheetData: ReturnType<typeof useTimesheetData>
  timeSheetDate: Moment
  employeeId: number
  store: Store
}

const TabsBreakdown = ({
  tabFromNavigation,
  timeSheetData,
  timeSheetDate,
  employeeId,
  store,
}: Props) => {
  const { cycle } = useAttendanceDateNavigator()
  const [openCompensationModal, setOpenCompensationModal] = React.useState(false)
  const [openPayrollWarningModal, setOpenPayrollWarningModal] = React.useState(false)
  const [responseData, setResponseData] = React.useState<
    AttendanceManualCompensation | undefined | null
  >()
  const [openResponseModal, setOpenResponseModal] = React.useState(false)
  const {
    aggregatedWorkedTime: workedTime,
    estimatedTimes,
    employeesSettings,
    overtimePools,
    timeBlocksBalance,
    featureByName,
    loading: isLoading,
    currentEmployeesSetting,
  } = timeSheetData

  const payrollCompensationEnabled =
    EmployeesSettingsUtils.hasPayrollCompensationEnabled(employeesSettings)

  const timeoffCompensationEnabled =
    EmployeesSettingsUtils.hasTimeoffCompensationEnabled(employeesSettings)

  // We make the compensate button only appear if the all
  // the compensation settings are monthly and the monthly view is opened and previous month or
  // the compensation settings are weekly and the weekly view is opened and previous week
  const samePeriodCompensation = Boolean(
    BalanceCarryoverUtils.samePeriodCompensation({
      compensationPeriod: currentEmployeesSetting?.extraTimeCompensationFrequency,
      viewCycle: cycle,
      timeSheetDate,
    })
  )

  const compensationEnabled = payrollCompensationEnabled || timeoffCompensationEnabled

  const { selectedTab, tabs, handleTabClick, closePanel } = useEmployeeSidepanelNavigation({
    tabFromNavigation,
    compensationEnabled,
    timeSheetDate,
    employeeId,
  })

  useAsyncData(
    async () =>
      policyAuthorizations.authorizePolicies('attendance.approve_period', [
        {
          id: employeeId,
          policyTargetClass: '::Employee',
        },
      ]),
    []
  )

  const { featureAvailability: hasCompensations } = useAttendanceFeatures(
    Features.compensations_management
  )

  const hasPayroll = TimesheetFeatures.PAYROLL in featureByName

  const canCompensate = hasCompensations || hasPayroll

  const onCompensate = () => {
    track(trackingEvents.timeTracking.sidePanelCompensatePressed, {})
    if (!canCompensate) setOpenPayrollWarningModal(true)
    else {
      setOpenCompensationModal(true)
    }
  }

  const getResponseData = (response: AttendanceManualCompensation | undefined | null) => {
    setResponseData(response)

    if (response) setOpenResponseModal(true)
  }

  const { pendingPayrollBlocks } = timeBlocksBalance.payrollCompensationPendingAndCompensated
  const canApprovePeriod = policyAuthorizations.for('attendance.approve_period', {
    id: employeeId,
    policyTargetClass: '::Employee',
  })
  const disableCompensateButton = isEmpty(pendingPayrollBlocks)
  const showCompensateButton =
    canApprovePeriod && payrollCompensationEnabled && samePeriodCompensation

  return (
    <Sidepanel
      open
      overlay={false}
      boxPadding={false}
      title={i18n.t('clock_in.sidepanel.title')}
      onClose={() => closePanel()}
      closeLabel={i18n.t('common.close')}
      footer={
        selectedTab === 'compensation' &&
        showCompensateButton && (
          <Box paddingY="s6" paddingX="s8" onClick={onCompensate}>
            <Button disabled={disableCompensateButton}>
              {i18n.t('clock_in.sidepanel.compensation.button_text')}
            </Button>
          </Box>
        )
      }
    >
      <Box
        paddingTop="s12"
        background="grey000White"
        position="sticky"
        style={{ top: '0', zIndex: '1' }}
      >
        <Tabs hasAdditionalPadding onTabClicked={handleTabClick} currentId={selectedTab}>
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.id} id={tab.id} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      <Box paddingTop="s12">
        <EmployeeTimeTrackingBreakdown
          selectedTab={selectedTab}
          overtimePools={overtimePools}
          timeBlocksBalance={timeBlocksBalance}
          store={store}
          workedTime={workedTime}
          estimatedTimes={estimatedTimes}
          employeeId={employeeId}
          loading={isLoading}
          payrollCompensationEnabled={payrollCompensationEnabled}
          timeoffCompensationEnabled={timeoffCompensationEnabled}
        />
      </Box>
      {openCompensationModal && (
        <CreateTimeBlockSupplementWizard
          employeeId={employeeId}
          getResponseData={getResponseData}
          loading={isLoading}
          onCancel={() => setOpenCompensationModal(false)}
          timeBlocksBalance={timeBlocksBalance}
          timeSheetDate={timeSheetDate}
        />
      )}
      {openPayrollWarningModal && (
        <PayrollSetupWarning onCloseModal={() => setOpenPayrollWarningModal(false)} />
      )}

      {openResponseModal && (
        <CompensationResponse
          response={responseData}
          onCloseModal={() => {
            setOpenResponseModal(false)
            closePanel()
          }}
        />
      )}
    </Sidepanel>
  )
}

export default observer(TabsBreakdown)
